import { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Printer, Loader2, FileDown, LayoutGrid } from "lucide-react";
import { cardImageStyle } from "@/components/Card3D";
import { CARD_SIZES, PAPER_SIZES, RARITY_NEON, computeLayout, generateCardSheetPdf } from "@/lib/cardPdf";
import CensorReviewDialog from "./CensorReviewDialog";

const BG_OPTIONS = [
  { key: "white", label: "White" },
  { key: "black", label: "Black" },
  { key: "transparent", label: "Transparent" },
];

const ORIENT_OPTIONS = [
  { key: "match", label: "Match each card" },
  { key: "portrait", label: "Force portrait" },
  { key: "landscape", label: "Force landscape" },
];

export default function PrintSheetDialog({ open, onOpenChange, cards }) {
  const [sizeKey, setSizeKey] = useState("playden");
  const [customW, setCustomW] = useState(56);
  const [customH, setCustomH] = useState(88);
  const [paperKey, setPaperKey] = useState("letter");
  const [landscape, setLandscape] = useState(false);
  const [orientationMode, setOrientationMode] = useState("match");
  const [gap, setGap] = useState(4);
  const [margin, setMargin] = useState(8);
  const [bleed, setBleed] = useState(0);
  const [expand, setExpand] = useState(false);
  const [mirror, setMirror] = useState(false);
  const [copies, setCopies] = useState(1);
  const [cropMarks, setCropMarks] = useState(true);
  const [cardBorder, setCardBorder] = useState(false);
  const [labels, setLabels] = useState(true);
  const [background, setBackground] = useState("white");
  const [censor, setCensor] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  const isCustom = sizeKey === "custom";
  const size = isCustom
    ? { key: "custom", w: Math.max(5, Number(customW) || 0), h: Math.max(5, Number(customH) || 0) }
    : (CARD_SIZES.find((s) => s.key === sizeKey) || CARD_SIZES[0]);
  // 2% outward expansion so a heat-pressed item of the true card size fits snugly.
  const ew = expand ? Math.round(size.w * 1.02 * 100) / 100 : size.w;
  const eh = expand ? Math.round(size.h * 1.02 * 100) / 100 : size.h;
  const paperBase = PAPER_SIZES.find((p) => p.key === paperKey) || PAPER_SIZES[0];
  const paper = landscape ? { w: paperBase.h, h: paperBase.w } : { w: paperBase.w, h: paperBase.h };

  const dimensions = useMemo(() => ({ cardW: ew, cardH: eh, paperW: paper.w, paperH: paper.h }), [ew, eh, paper.w, paper.h]);
  const placement = useMemo(() => ({ gap, margin, orientationMode, bleed }), [gap, margin, orientationMode, bleed]);
  const layout = useMemo(() => computeLayout({ cards, ...dimensions, ...placement, copies: Number(copies) || 1 }), [cards, dimensions, placement, copies]);

  const totalCards = (cards?.length || 0) * (Number(copies) || 1);
  const pages = layout.pages;
  const fits = layout.fits;

  // Resolve a card's printed orientation for the preview thumbnails.
  const shortE = Math.min(size.w, size.h);
  const longE = Math.max(size.w, size.h);
  const cardIsLandscape = (c) => orientationMode === "landscape" || (orientationMode === "match" && c?.orientation === "landscape");
  const previewRatio = (c) => (cardIsLandscape(c) ? `${longE} / ${shortE}` : `${shortE} / ${longE}`);

  const doGenerate = async (censorMap) => {
    if (!cards?.length) { toast.error("Select at least one card"); return; }
    if (!fits) { toast.error("Card size too large for this paper — reduce size or margins"); return; }
    setBusy(true);
    setProgress(0);
    try {
      const effectiveCards = censorMap
        ? cards.map((c) => (c.front_image && censorMap[c.front_image] ? { ...c, front_image: censorMap[c.front_image] } : c))
        : cards;
      const res = await generateCardSheetPdf({
        cards: effectiveCards,
        cardW: ew,
        cardH: eh,
        paperW: paper.w,
        paperH: paper.h,
        gap,
        margin,
        cropMarks,
        cardBorder,
        background,
        orientationMode,
        bleed,
        labels,
        mirror,
        copies: Number(copies) || 1,
        filename: `playden-cards-${size.key}.pdf`,
        onProgress: (done, total) => setProgress(Math.round((done / total) * 100)),
      });
      toast.success(`PDF ready — ${res.pages} page${res.pages === 1 ? "" : "s"}, ${res.count} card${res.count === 1 ? "" : "s"}`);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.message || "PDF export failed");
    } finally {
      setBusy(false);
    }
  };

  const onGenerateClick = () => {
    if (!cards?.length) { toast.error("Select at least one card"); return; }
    if (!fits) { toast.error("Card size too large for this paper — reduce size or margins"); return; }
    if (censor && cards.some((c) => c.front_image)) {
      setReviewOpen(true);
    } else {
      doGenerate(null);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <DialogContent className="bg-[hsl(222_22%_8%)] border-white/10 max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="print-sheet-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-[0.08em] flex items-center gap-2">
            <Printer className="h-5 w-5 text-cyan-300" /> PRINT SHEET — {cards?.length || 0} CARD{(cards?.length || 0) === 1 ? "" : "S"}
          </DialogTitle>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-4 mt-1">
          {/* Left: options */}
          <div className="space-y-3">
            <div>
              <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Card Size</Label>
              <Select value={sizeKey} onValueChange={setSizeKey}>
                <SelectTrigger data-testid="print-size-select" className="mt-1 bg-[hsl(220_18%_12%)] border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[hsl(220_18%_10%)] border-white/10 max-h-72">
                  {CARD_SIZES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                  <SelectItem value="custom">Custom size…</SelectItem>
                </SelectContent>
              </Select>
              {isCustom && (
                <div className="mt-2 grid grid-cols-2 gap-2" data-testid="print-custom-size">
                  <div>
                    <Label className="text-white/50 text-[10px] uppercase tracking-[0.2em]">Width (mm)</Label>
                    <Input data-testid="print-custom-w" type="number" min={5} max={200} step={0.5} value={customW}
                      onChange={(e) => setCustomW(e.target.value)} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
                  </div>
                  <div>
                    <Label className="text-white/50 text-[10px] uppercase tracking-[0.2em]">Height (mm)</Label>
                    <Input data-testid="print-custom-h" type="number" min={5} max={280} step={0.5} value={customH}
                      onChange={(e) => setCustomH(e.target.value)} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
                  </div>
                  <div className="col-span-2 text-[10px] text-white/40">Artwork is scaled to fit this size without distortion (cropped, never stretched).</div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Paper</Label>
                <Select value={paperKey} onValueChange={setPaperKey}>
                  <SelectTrigger data-testid="print-paper-select" className="mt-1 bg-[hsl(220_18%_12%)] border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[hsl(220_18%_10%)] border-white/10">
                    {PAPER_SIZES.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Background</Label>
                <Select value={background} onValueChange={setBackground}>
                  <SelectTrigger data-testid="print-bg-select" className="mt-1 bg-[hsl(220_18%_12%)] border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[hsl(220_18%_10%)] border-white/10">
                    {BG_OPTIONS.map((b) => <SelectItem key={b.key} value={b.key}>{b.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center justify-between hud-pill rounded-lg px-3 py-2">
              <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Landscape Paper</Label>
              <Switch data-testid="print-landscape-switch" checked={landscape} onCheckedChange={setLandscape} />
            </div>

            <div>
              <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Card Orientation</Label>
              <Select value={orientationMode} onValueChange={setOrientationMode}>
                <SelectTrigger data-testid="print-orientation-select" className="mt-1 bg-[hsl(220_18%_12%)] border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[hsl(220_18%_10%)] border-white/10">
                  {ORIENT_OPTIONS.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="text-[10px] text-white/40 mt-1">“Match each card” keeps portrait cards portrait and landscape cards landscape.</div>
            </div>

            <div>
              <div className="flex items-center justify-between"><Label className="text-white/60 text-[10px] uppercase tracking-[0.2em]">Gap between cards</Label><span className="font-mono text-[10px] text-white/40">{gap} mm</span></div>
              <Slider data-testid="print-gap-slider" value={[gap]} min={0} max={12} step={0.5} onValueChange={([v]) => setGap(v)} className="mt-2" />
            </div>
            <div>
              <div className="flex items-center justify-between"><Label className="text-white/60 text-[10px] uppercase tracking-[0.2em]">Page margin</Label><span className="font-mono text-[10px] text-white/40">{margin} mm</span></div>
              <Slider data-testid="print-margin-slider" value={[margin]} min={0} max={25} step={1} onValueChange={([v]) => setMargin(v)} className="mt-2" />
            </div>
            <div>
              <div className="flex items-center justify-between"><Label className="text-white/60 text-[10px] uppercase tracking-[0.2em]">Bleed (past cut line)</Label><span className="font-mono text-[10px] text-white/40">{bleed} mm</span></div>
              <Slider data-testid="print-bleed-slider" value={[bleed]} min={0} max={5} step={0.5} onValueChange={([v]) => setBleed(v)} className="mt-2" />
              <div className="text-[10px] text-white/40 mt-1">Extends artwork past the crop marks so slightly-off cuts never leave a white edge. 2–3 mm is typical.</div>
            </div>
            <div>
              <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Copies per card</Label>
              <Input data-testid="print-copies-input" type="number" min={1} max={50} value={copies} onChange={(e) => setCopies(Math.max(1, Math.min(50, Number(e.target.value) || 1)))} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
            </div>

            <div className="flex items-center justify-between hud-pill rounded-lg px-3 py-2">
              <div>
                <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Expand 2% (snug fit)</Label>
                <div className="text-[10px] text-white/40 mt-0.5">Scales the card 2% larger so a heat-pressed item of the true size fits snugly.</div>
              </div>
              <Switch data-testid="print-expand-switch" checked={expand} onCheckedChange={setExpand} />
            </div>
            <div className="flex items-center justify-between hud-pill rounded-lg px-3 py-2">
              <div>
                <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Mirror (heat-press)</Label>
                <div className="text-[10px] text-white/40 mt-0.5">Flips each card horizontally so it reads correctly after heat-transfer.</div>
              </div>
              <Switch data-testid="print-mirror-switch" checked={mirror} onCheckedChange={setMirror} />
            </div>
            <div className="flex items-center justify-between hud-pill rounded-lg px-3 py-2">
              <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Cut / crop marks</Label>
              <Switch data-testid="print-cropmarks-switch" checked={cropMarks} onCheckedChange={setCropMarks} />
            </div>
            <div className="flex items-center justify-between hud-pill rounded-lg px-3 py-2">
              <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Card border</Label>
              <Switch data-testid="print-border-switch" checked={cardBorder} onCheckedChange={setCardBorder} />
            </div>
            <div className="flex items-center justify-between hud-pill rounded-lg px-3 py-2">
              <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Rarity &amp; name labels</Label>
              <Switch data-testid="print-labels-switch" checked={labels} onCheckedChange={setLabels} />
            </div>
            <div className="flex items-center justify-between hud-pill rounded-lg px-3 py-2 border border-amber-500/20">
              <div>
                <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Censor explicit parts (checkered)</Label>
                <div className="text-[10px] text-white/40 mt-0.5">Covers nipples, genitalia &amp; exposed anus with a B/W checker in the PDF only — redeemed cards stay full. You review &amp; adjust before export.</div>
              </div>
              <Switch data-testid="print-censor-switch" checked={censor} onCheckedChange={setCensor} />
            </div>
          </div>

          {/* Right: layout summary + preview */}
          <div className="space-y-3">
            <div className="hud-pill rounded-lg p-3 space-y-2">
              <div className="flex items-center gap-2 text-cyan-200 text-xs uppercase tracking-[0.2em]"><LayoutGrid className="h-4 w-4" /> Layout</div>
              {fits ? (
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div><div className="font-mono text-xl text-white" data-testid="print-per-page">{layout.firstPageCount}</div><div className="text-[10px] text-white/40 uppercase tracking-wider">1st page</div></div>
                  <div><div className="font-mono text-xl text-white" data-testid="print-total-cards">{totalCards}</div><div className="text-[10px] text-white/40 uppercase tracking-wider">Cards</div></div>
                  <div><div className="font-mono text-xl text-white" data-testid="print-pages">{pages}</div><div className="text-[10px] text-white/40 uppercase tracking-wider">Pages</div></div>
                </div>
              ) : (
                <div className="text-amber-300 text-sm" data-testid="print-nofit-warning">Card is too large for this paper. Lower the margin or pick a smaller size.</div>
              )}
              <div className="text-[10px] text-white/40 font-mono">{ew} × {eh} mm edges{expand ? " (2% expanded)" : ""} · {paper.w} × {paper.h} mm sheet</div>
            </div>

            <div>
              <Label className="text-white/60 text-[10px] uppercase tracking-[0.2em]">Selected artwork</Label>
              <div className="mt-1.5 grid grid-cols-4 gap-1.5 max-h-64 overflow-y-auto pr-1 rounded-lg items-start" data-testid="print-preview-grid">
                {(cards || []).map((c, i) => (
                  <div key={c.id || i} className="relative overflow-hidden rounded border border-white/10"
                    style={{ aspectRatio: previewRatio(c), background: background === "black" ? "#000" : background === "white" ? "#fff" : "transparent", transform: mirror ? "scaleX(-1)" : undefined }}>
                    {c.front_image
                      ? <img src={c.front_image} alt="" className="absolute inset-0 w-full h-full" style={cardImageStyle(c)} />
                      : <div className="absolute inset-0 flex items-center justify-center text-[8px] text-white/30">No art</div>}
                    {labels && (
                      <>
                        <span className="absolute top-0.5 left-1 font-bold uppercase tracking-wider leading-none rounded-full px-1 py-0.5" style={{ fontSize: "6px", color: RARITY_NEON[c.rarity] || "#fff", background: "rgba(6,10,16,0.45)", border: `0.5px solid ${RARITY_NEON[c.rarity] || "#fff"}`, textShadow: `0 0 3px ${RARITY_NEON[c.rarity] || "#fff"}` }}>{c.rarity}</span>
                        {c.edition_number && c.edition_count && (
                          <span className="absolute top-0.5 right-1 font-bold tracking-wider leading-none rounded-full px-1 py-0.5" style={{ fontSize: "6px", color: RARITY_NEON[c.rarity] || "#fff", background: "rgba(6,10,16,0.45)", border: `0.5px solid ${RARITY_NEON[c.rarity] || "#fff"}`, textShadow: `0 0 3px ${RARITY_NEON[c.rarity] || "#fff"}` }}>{c.edition_number}/{c.edition_count}</span>
                        )}
                        {c.code && (
                          <span className="absolute left-1 right-1 font-bold text-center truncate leading-none rounded" style={{ bottom: "8px", fontSize: "5px", fontFamily: "monospace", color: RARITY_NEON[c.rarity] || "#fff", background: "#05070c", border: `0.5px solid ${RARITY_NEON[c.rarity] || "#fff"}`, padding: "1px 2px", textShadow: `0 0 2px ${RARITY_NEON[c.rarity] || "#fff"}` }}>{c.code}</span>
                        )}
                        <span className="absolute left-1 right-1 text-center truncate leading-none rounded-full" style={{ bottom: c.code ? "20px" : "2px", fontSize: "5px", color: RARITY_NEON[c.rarity] || "#fff", background: "rgba(0,0,0,0.3)", padding: "0.5px 2px", opacity: 0.9, textShadow: `0 0 3px ${RARITY_NEON[c.rarity] || "#fff"}` }}>{c.title}</span>
                      </>
                    )}
                  </div>
                ))}
                {(cards || []).length === 0 && <div className="col-span-4 text-white/40 text-xs py-6 text-center">No cards selected.</div>}
              </div>
            </div>
          </div>
        </div>

        {busy && (
          <div className="w-full h-1.5 rounded bg-white/10 overflow-hidden mt-1">
            <div className="h-full bg-cyan-400 transition-all duration-150" style={{ width: `${progress}%` }} />
          </div>
        )}

        <Button data-testid="print-generate-button" onClick={onGenerateClick} disabled={busy || !fits || !(cards?.length)} className="w-full bg-cyan-400 text-black hover:bg-cyan-300 mt-1">
          {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Rendering… {progress}%</> : <><FileDown className="h-4 w-4 mr-2" /> {censor ? "Review & download PDF" : `Download PDF`} ({pages} page{pages === 1 ? "" : "s"})</>}
        </Button>
      </DialogContent>
    </Dialog>
    <CensorReviewDialog
      open={reviewOpen}
      onOpenChange={(o) => { if (!busy) setReviewOpen(o); }}
      cards={cards}
      onConfirm={(map) => { setReviewOpen(false); doGenerate(map); }}
    />
    </>
  );
}
